import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import CollapsibleSubmenu from './CollapsibleSubmenu';
import { useReports } from '../hooks/useReports';

const SideNav = () => {
  const appVersion = import.meta.env.VITE_APP_VERSION || 'dev';
  const STORAGE_KEY = `v${appVersion}_adap_sidenav-collapsed-state_${window.location.hostname}`;
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : false; // Default to false if not found
    } catch (e) {
      console.error("Failed to load side nav collapsed state from local storage", e);
      return false; // Default to false on error
    }
  });
  const [mapsSubmenuOpen, setMapsSubmenuOpen] = useState(false);
  const [operationsSubmenuOpen, setOperationsSubmenuOpen] = useState(false);
  const { reportIds: reports, loading: reportsLoading } = useReports();

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(isCollapsed));
  }, [isCollapsed, STORAGE_KEY]);

  useEffect(() => {
    const syncSubmenuState = () => {
      if (reportsLoading) return;

      const path = location.pathname;
      const mapsReports = [
        reports['LDC Zone Map'],
        reports['FSA Map'],
        reports['FSA EV'],
        reports['Net Meter Map']
      ].filter(Boolean).map(id => `/report/${id}`);

      const opsReports = [
        reports['MDMR Dashboard Reports'],
        reports['FSA Time Series'],
      ].filter(Boolean).map(id => `/report/${id}`);

      if (mapsReports.includes(path)) {
        setMapsSubmenuOpen(true);
      } else if (opsReports.includes(path)) {
        setOperationsSubmenuOpen(true);
      }
    };

    syncSubmenuState();
  }, [location.pathname, reports, reportsLoading]);

  const handleLogout = () => {
    window.location.href = "/logout";
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <nav className={`nav-menu ${isCollapsed ? 'collapsed' : ''}`}>
      <button id="nav-collapse-toggle" className="nav-collapse-toggle" onClick={toggleCollapse}>
        <i className={isCollapsed ? "fa fa-bars" : "fa fa-chevron-left"}></i>
      </button>
      <h3 className=" pt-3 pb-2">Navigation</h3>
      <ul className="mb-0">
        <li>
          <NavLink to="/" end className="home-link d-flex align-items-center nav-link">
            <span className="dropdown-label">
              <span className="icon"><i className="fa fa-home"></i></span>
              <span className="label">Home</span>
            </span>
          </NavLink>
        </li>
        <li>
          <a href="#mapsSubmenu" className="dropdown-toggle nav-link" onClick={(e) => { e.preventDefault(); setMapsSubmenuOpen(!mapsSubmenuOpen); }}>
            <span className="dropdown-label">
              <span className="icon"><i className="fa-solid fa-map-location-dot"></i></span>
              <span className="label">Maps</span>
            </span>
            <span className="dropdown-chevron" aria-hidden="true"></span>
          </a>
          <CollapsibleSubmenu isOpen={mapsSubmenuOpen}>
            <ul className="list-unstyled">
              <li><NavLink to={reports['LDC Zone Map'] ? `/report/${reports['LDC Zone Map']}` : '#'} className="nav-link"><span className="icon"><i className="fa-solid fa-layer-group"></i></span><span className="label">LDC Zone Map</span></NavLink></li>
              <li><NavLink to={reports['FSA Map'] ? `/report/${reports['FSA Map']}` : '#'} className="nav-link"><span className="icon"><i className="fa-solid fa-location-dot"></i></span><span className="label">FSA Map</span></NavLink></li>
              <li><NavLink to={reports['FSA EV'] ? `/report/${reports['FSA EV']}` : '#'} className="nav-link"><span className="icon"><i className="fa-solid fa-battery-full"></i></span><span className="label">FSA EV Map</span></NavLink></li>
              <li><NavLink to={reports['Net Meter Map'] ? `/report/${reports['Net Meter Map']}` : '#'} className="nav-link"><span className="icon"><i className="fa-solid fa-bolt"></i></span><span className="label">Net Meter Map</span></NavLink></li>
            </ul>
          </CollapsibleSubmenu>
        </li>
        <li>
          <a href="#operationsSubmenu" className="dropdown-toggle nav-link" onClick={(e) => { e.preventDefault(); setOperationsSubmenuOpen(!operationsSubmenuOpen); }}>
            <span className="dropdown-label">
              <span className="icon"><i className="fa-solid fa-chart-line"></i></span>
              <span className="label">Operations</span>
            </span>
            <span className="dropdown-chevron" aria-hidden="true"></span>
          </a>
          <CollapsibleSubmenu isOpen={operationsSubmenuOpen}>
            <ul className="list-unstyled">
              <li><NavLink to={reports['MDMR Dashboard Reports'] ? `/report/${reports['MDMR Dashboard Reports']}` : '#'} className="nav-link"><span className="icon"><i className="fa-solid fa-tachometer-alt"></i></span><span className="label">Operations Overview</span></NavLink></li>
              <li><NavLink to={reports['FSA Time Series'] ? `/report/${reports['FSA Time Series']}` : '#'} className="nav-link"><span className="icon"><i className="fa-solid fa-chart-area"></i></span><span className="label">FSA Time Series</span></NavLink></li>
            </ul>
          </CollapsibleSubmenu>
        </li>
        <li>
          <NavLink to="/genie" className="nav-link">
            <span className="dropdown-label">
              <span className="icon"><i className="fa-solid fa-wand-magic-sparkles"></i></span>
              <span className="label">Genie</span>
            </span>
          </NavLink>
        </li>
      </ul>
      <div style={{ flex: 1 }}></div>
      <ul className="mb-0">
        <li>
          <button onClick={handleLogout} className="logout-link" style={{ border: 'none', background: 'none', color: 'inherit', padding: '0', margin: '0', width: '100%' }}>
            <span className="dropdown-label">
              <span className="icon"><i className="fa-solid fa-power-off"></i></span>
              <span className="label">Logout</span>
            </span>
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default SideNav;
